import { useEffect, useState } from 'react';
import { formatInstant, useEffectiveTimeZone } from '../../lib/time-format';

/**
 * "as of HH:MM · updated Ns ago" freshness stamp (ADR-103 S4d / ADR-110).
 *
 * WHY THIS EXISTS. The Flow Validator refreshes MANUALLY by design — auto-polling
 * would burn the Tinybird query budget (ADR-093), so it is deliberately not
 * added. But a manual snapshot with no staleness cue reads as a live feed, which
 * is exactly what made T14 UAT think the snippet was dead when it was fine. This
 * stamp makes the snapshot's age legible so a static view is never mistaken for a
 * live one. It is DISPLAY-ONLY: it ticks a client-side clock from the last
 * successful fetch time and costs NO Tinybird query.
 *
 * `at` is the epoch-ms of the last successful load (null before the first one).
 */
export function FreshnessStamp({ at }: { at: number | null }) {
  // Re-render on a light cadence so "updated Ns ago" stays current WITHOUT any
  // network call. 15s is fine — the number is an approximation, not a metric.
  const [, tick] = useState(0);
  const zone = useEffectiveTimeZone();
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  if (at == null) return null;

  const asOf = formatInstant(new Date(at).toISOString(), zone, 'time').text;
  const secs = Math.max(0, Math.round((Date.now() - at) / 1000));
  const ago =
    secs < 60 ? `${secs}s ago`
    : secs < 3600 ? `${Math.round(secs / 60)}m ago`
    : `${Math.round(secs / 3600)}h ago`;

  return (
    <span className="telp-fresh" title={`Last successful load at ${asOf}`}>
      as of <b>{asOf}</b>&nbsp;· updated {ago}
    </span>
  );
}
